import React, { useState, useEffect } from 'react';
import { 
  Building2, Globe, ArrowUpRight, AlertTriangle, Clock, ArrowUp, ArrowDown, ArrowUpDown, Shield 
} from 'lucide-react';
import { fallbackExchangeData } from '../utils/fallbackExchangeData';
import { initDB, updateExchanges, getAllExchanges, getLastUpdateTime } from '../db/exchangeDB';

interface Exchange {
  id: string;
  name: string;
  year_established: number;
  country: string;
  description: string;
  url: string;
  image: string;
  trust_score: number;
  trust_score_rank: number;
  trade_volume_24h_btc: number;
  trade_volume_24h_btc_normalized: number;
  last_updated?: number;
}

interface SortConfig {
  key: keyof Exchange;
  direction: 'asc' | 'desc';
}

export function Exchanges() {
  const [exchanges, setExchanges] = useState<Exchange[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isUsingFallback, setIsUsingFallback] = useState(false);
  const [nextUpdate, setNextUpdate] = useState<number>(0);
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: 'trust_score',
    direction: 'desc'
  });

  useEffect(() => {
    const initializeDB = async () => {
      try {
        await initDB();
        await fetchExchanges();
      } catch (err) {
        console.warn('Using fallback exchange data');
        setExchanges(fallbackExchangeData);
        setIsUsingFallback(true);
        setError('Unable to initialize exchange data storage');
      } finally {
        setIsLoading(false);
      }
    };

    initializeDB();
  }, []);

  useEffect(() => {
    const timer = setInterval(async () => {
      const lastUpdateTime = await getLastUpdateTime();
      if (lastUpdateTime) {
        const now = Date.now();
        const nextUpdateTime = lastUpdateTime + 60 * 60 * 1000;
        setNextUpdate(Math.max(0, nextUpdateTime - now));
      }
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const fetchExchanges = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(
        'https://api.coingecko.com/api/v3/exchanges?per_page=100',
        {
          headers: {
            'Accept': 'application/json',
            'Cache-Control': 'no-cache'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`API responded with status: ${response.status}`);
      }

      const data = await response.json();
      if (data && data.length > 0) {
        // Sort exchanges by trust score and volume
        const sortedData = data.sort((a: Exchange, b: Exchange) => {
          // Primary sort by trust score
          const trustScoreDiff = b.trust_score - a.trust_score;
          if (trustScoreDiff !== 0) return trustScoreDiff;
          
          // Secondary sort by trading volume
          return b.trade_volume_24h_btc - a.trade_volume_24h_btc;
        });

        setExchanges(sortedData);
        setIsUsingFallback(false);
        setError(null);

        // Update IndexedDB
        await updateExchanges();
      } else {
        throw new Error('No exchange data available');
      }
    } catch (err) {
      console.warn('Using fallback exchange data');
      // Try to get data from IndexedDB first
      const cachedData = await getAllExchanges();
      if (cachedData.length > 0) {
        setExchanges(cachedData);
      } else {
        setExchanges(fallbackExchangeData);
      }
      setIsUsingFallback(true);
      setError('Unable to fetch real-time exchange data. Using cached data.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatTimeRemaining = (ms: number) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  };

  const handleSort = (key: keyof Exchange) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const getSortIcon = (key: keyof Exchange) => {
    if (sortConfig.key !== key) return <ArrowUpDown className="w-4 h-4 opacity-50" />;
    return sortConfig.direction === 'asc' ? 
      <ArrowUp className="w-4 h-4" /> : 
      <ArrowDown className="w-4 h-4" />;
  };

  const getTrustScoreColor = (score: number) => {
    if (score >= 8) return 'bg-green-100 text-green-800';
    if (score >= 6) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const sortedExchanges = [...exchanges].sort((a, b) => {
    const multiplier = sortConfig.direction === 'asc' ? 1 : -1;
    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return aValue.localeCompare(bValue) * multiplier;
    }

    // Special handling for trust score to consider volume as secondary sort
    if (sortConfig.key === 'trust_score' && aValue === bValue) {
      return (b.trade_volume_24h_btc - a.trade_volume_24h_btc) * multiplier;
    }

    return ((aValue as number) - (bValue as number)) * multiplier;
  });

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            {[...Array(6)].map((_, index) => (
              <div key={index} className="bg-gray-200 rounded-xl h-64"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Building2 className="w-6 h-6 text-blue-500" />
          Cryptocurrency Exchanges
        </h1>
        
        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-500 flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Next update in: {formatTimeRemaining(nextUpdate)}
          </div>
        </div>
      </div>

      {isUsingFallback && error && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
          <p className="text-sm text-amber-700">{error}</p>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('trust_score_rank')}
                >
                  <div className="flex items-center gap-2">
                    Rank {getSortIcon('trust_score_rank')}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center gap-2">
                    Exchange {getSortIcon('name')}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('trust_score')}
                >
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    Trust Score {getSortIcon('trust_score')}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('trade_volume_24h_btc')}
                >
                  <div className="flex items-center gap-2">
                    24h Volume (BTC) {getSortIcon('trade_volume_24h_btc')}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('year_established')}
                >
                  <div className="flex items-center gap-2">
                    Year Est. {getSortIcon('year_established')}
                  </div>
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedExchanges.map((exchange) => (
                <tr 
                  key={exchange.id} 
                  className={`hover:bg-gray-50 ${
                    exchange.trust_score >= 8 ? 'bg-green-50/30' :
                    exchange.trust_score >= 6 ? 'bg-yellow-50/30' :
                    'bg-red-50/30'
                  }`}
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    #{exchange.trust_score_rank}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <img 
                        src={exchange.image} 
                        alt={exchange.name} 
                        className="w-8 h-8 rounded-full"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = 'https://via.placeholder.com/32?text=' + exchange.name.charAt(0);
                        }}
                      />
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{exchange.name}</div>
                        <div className="text-sm text-gray-500">{exchange.country || 'Global'}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTrustScoreColor(exchange.trust_score)}`}>
                      <Shield className="w-3 h-3 mr-1" />
                      {exchange.trust_score}/10
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {exchange.trade_volume_24h_btc.toLocaleString(undefined, {
                      maximumFractionDigits: 2
                    })} BTC
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {exchange.year_established || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <a
                      href={exchange.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-900 flex items-center justify-end gap-1"
                    >
                      Visit
                      <ArrowUpRight className="w-4 h-4" />
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}